import type { ReactNode } from 'react';

import { BoardSectionEmbedWidget } from '#components/Board/Section/BoardSectionEmbedWidget';
import { BoardSectionItemHeroNewsteaser } from '#components/Board/Section/Items/BoardSectionItemHeroNewsteaser';
import { Link } from '#components/Link/Link';
import { BreakpointMedia } from '#cutils/breakpoints/media';
import type { SectionSubjectNewsWeather } from '#domains/resolver/board-section-data';
import { Tracktor } from 'react-tracktor';

import { IMAGE_SIZE_TWO_THIRD } from '../../BoardImageSizes';
import { BoardSectionItem } from '../BoardSectionItem';
import { BoardSectionKiAssistant } from '../BoardSectionKiAssistant';
import { BoardSectionItemRichText } from '../Items/BoardSectionItemRichText';
import { BoardSectionTileRow } from '../Tile/TileRow';
import { BoardSectionTileSection } from '../Tile/TileSection';

import styles from './BoardSectionTileSubjectsNewsWeather.module.scss';

type Props = {
	section: SectionSubjectNewsWeather;
};

export function BoardSectionTileSubjectsNewsWeather({ section }: Props) {
	const { left, right } = section.items;

	if (left.type === 'empty' || right.some((teaser) => teaser.type === 'empty')) {
		return null;
	}

	const { kiAssistant } = section;
	const mobilePosition = kiAssistant?.position;

	const mobileWidget = kiAssistant ? (
		<BreakpointMedia lessThan="lg">
			{(className, renderChildren) =>
				renderChildren ? (
					<BoardSectionKiAssistant kiAssistant={kiAssistant} className={`${styles.widget} ${className}`} enableViewMode={false} />
				) : null
			}
		</BreakpointMedia>
	) : null;

	// Positions 1–3 embed the widget inside BoardSectionItemHeroNewsteaser (before index 1, 2, or after the last).
	const embedBeforeIndex = mobilePosition !== undefined && mobilePosition >= 1 && mobilePosition <= 3 ? mobilePosition - 1 : undefined;
	const embedContent = embedBeforeIndex !== undefined ? mobileWidget : undefined;

	let headline: ReactNode;

	const title = section.title;

	if (!title?.link) {
		headline = title?.text;
	} else {
		headline = <Link href={title.link}>{title.text}</Link>;
	}

	return (
		<>
			<BoardSectionTileSection>
				{section.title?.showHeroTitleForMobile && (
					<div className={`heading2 ${styles.newsTeaserHeadline} ${styles.showMobileTitle}`} data-theme="dark">
						{headline}
					</div>
				)}
				<BoardSectionTileRow>
					{mobilePosition === 0 && mobileWidget}

					<Tracktor trackingData={{ data: { inSectionPlacement: 0 } }}>
						<BoardSectionItem className={styles.hero} teaser={left} isLarge={true} isPriority={true} sizes={IMAGE_SIZE_TWO_THIRD} />
					</Tracktor>

					<div className={styles.rightRoot}>
						<BoardSectionItemHeroNewsteaser
							teasers={right}
							title={section.title}
							embedBeforeIndex={embedBeforeIndex}
							embedContent={embedContent}
							className={kiAssistant ? styles.teaserFixedHeight : undefined}
						/>

						<BreakpointMedia greaterThanOrEqual={mobilePosition === 4 || !kiAssistant ? 'xs' : 'lg'}>
							{(className, renderChildren) =>
								renderChildren ? (
									<BoardSectionEmbedWidget
										kiAssistant={kiAssistant}
										className={`${className} ${kiAssistant === undefined ? styles.radio : ''}`}
									/>
								) : null
							}
						</BreakpointMedia>
					</div>
				</BoardSectionTileRow>
			</BoardSectionTileSection>
			<BoardSectionItemRichText introduction={section.introduction} />
		</>
	);
}
